import { format, parseISO } from "date-fns";
import { useState } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { HomeMetricsResponse, Period } from "@go-novus/admin-api";
import { Panel, Skeleton } from "@go-novus/ui";

import * as m from "#/paraglide/messages";

import {
  bucketTrend,
  type TrendBucket,
  type TrendGrain,
} from "../lib/bucket-trend";

type SeriesKey = "aiResolved" | "conversations";

/** 线宽、点半径、留白都是 Home.dc.html 里的数，改一个就对不上设计稿。 */
const LINE_WIDTH = 2.2;
/** 逐日时点密，小点才不糊成一片；周桶只有四五个点，点是读数的锚，放到 8px 直径。 */
const DOT_RADIUS: Record<TrendGrain, number> = { day: 2.6, week: 4 };
const DOT_STROKE_WIDTH = 1.8;
const ACTIVE_DOT_GROWTH = 1.5;
const AREA_OPACITY = 0.07;
/** 悬停图例时另一条压到这个透明度，只留下要看的那条。 */
const DIMMED_OPACITY = 0.1;
/**
 * 右留白要容得下最后一个日期标签的一半，不然 Recharts 居中的刻度会被卡片裁掉；
 * 周桶还要再容下端点数值。
 */
const CHART_MARGIN: Record<
  TrendGrain,
  { bottom: number; left: number; right: number; top: number }
> = {
  day: { bottom: 0, left: 0, right: 24, top: 10 },
  week: { bottom: 0, left: 0, right: 44, top: 10 },
};
/** 纵轴宽度要装得下三位数刻度，再留出刻度与网格线之间的空隙。 */
const AXIS_WIDTH = 34;
const GRID_ROWS = 4;
/** 纵轴顶格取到 20 的倍数，四条网格线上的刻度才都是整数。 */
const AXIS_STEP = 20;
/** x 轴最多标这么多个日期；近 30 天于是每 5 天标一个。 */
const MAX_DATE_LABELS = 7;
/** 点太密就只留折线，近 7 天才画数据点。 */
const MAX_DOTS = 10;
const DAY_FORMAT = "MMM d";
/**
 * 端点数值贴在最后一个点右边。paint-order 让描边先画、填色后画，等于给字加了一圈
 * 背景色光晕，压住底下的网格线和另一条线；不这么做就得自己估文字宽度垫一块底板，
 * 估宽在等宽字体换字号时必错。
 */
const END_LABEL_STYLE = {
  fill: "var(--content-fg-strong)",
  offset: 8,
  paintOrder: "stroke",
  position: "right",
  stroke: "var(--bg-surface-base)",
  strokeWidth: 4,
} as const;

const AXIS_COLOR = "var(--border-surface-default)";
const TICK_FILL = { fill: "var(--content-fg-subtle)" };
const DASHED = "2 4";

const SERIES_COLOR: Record<SeriesKey, string> = {
  aiResolved: "var(--content-vibrant-warning-default)",
  conversations: "var(--content-vibrant-success-default)",
};

// 色块的形状跟着图上的画法走：会话数是面积，画成方块；AI 解决是纯线，画成细线。
// 两条线的颜色在红绿色盲下几乎是同一个（ΔE 6.9），只靠颜色对不上号，形状是第二条线索。
const SERIES_SWATCH: Record<SeriesKey, string> = {
  aiResolved:
    "h-0.75 w-3.5 flex-none rounded-xs bg-[var(--content-vibrant-warning-default)]",
  conversations:
    "h-2.5 w-3.5 flex-none rounded-xs bg-[var(--content-vibrant-success-default)]",
};

// 收起的那条线整体调淡。外壳照 Home.dc.html 画成一颗描边胶囊，形状与
// SegmentedControl 相近但不是同一个控件：图例是多选开关，设计系统里没有对应件。
// 高度是 min-h-6：设计稿的 19.5px 过不了 WCAG 2.2 的 24px 目标尺寸，而
// ui-style.md 的 min-h-control-sm（32px）是给成排控件对齐用的，这里旁边没有控件，
// 用它会比稿子高一截。两头都不占，取能过 AA 的最小值。
const LEGEND_CLASS =
  "inline-flex min-h-6 cursor-pointer items-center gap-1.5 rounded-[var(--radius)] border-0 bg-transparent px-2 text-micro leading-none font-semibold text-[var(--content-fg-subtle)] opacity-38 outline-[var(--border-accent-default)] hover:bg-[var(--bg-surface-base)] hover:text-[var(--content-fg-strong)] focus-visible:outline-2 aria-pressed:opacity-100";

function formatDay(date: string): string {
  return format(parseISO(date), DAY_FORMAT);
}

/** 逐日时起止是同一天，只写一个日期；周桶写整段，免得被读成某一天的数。 */
function formatSpan(bucket: TrendBucket): string {
  return bucket.start === bucket.end
    ? formatDay(bucket.start)
    : m.home_trend_tooltip_range({
        end: formatDay(bucket.end),
        start: formatDay(bucket.start),
      });
}

/**
 * 横轴要标哪几天。交给 Recharts 的数字 interval 会把末尾那天丢掉——最后一天是
 * 商家最关心的一天，所以自己算，首尾必标。
 */
function readDateTicks(buckets: readonly TrendBucket[]): string[] {
  const step = Math.max(1, Math.ceil(buckets.length / MAX_DATE_LABELS));
  const lastIndex = buckets.length - 1;

  return buckets
    .filter((_, index) => index % step === 0 || index === lastIndex)
    .map((bucket) => bucket.start);
}

/** 两条线都要框得住：只按会话数取顶格的话，AI 解决数更高时那条线会被裁到框外。 */
function readTicks(buckets: readonly TrendBucket[]): number[] {
  const peak = Math.max(
    AXIS_STEP,
    ...buckets.map((bucket) =>
      Math.max(bucket.aiResolved, bucket.conversations),
    ),
  );
  const axisMax = Math.ceil(peak / AXIS_STEP) * AXIS_STEP;

  return Array.from(
    { length: GRID_ROWS + 1 },
    (_, row) => (axisMax / GRID_ROWS) * row,
  );
}

function readDot(series: SeriesKey, radius: number, opacity: number) {
  return {
    fill: "var(--bg-surface-base)",
    opacity,
    r: radius,
    stroke: SERIES_COLOR[series],
    strokeWidth: DOT_STROKE_WIDTH,
  };
}

interface TrendTooltipProps {
  bucket: TrendBucket;
  visible: Record<SeriesKey, boolean>;
}

function TrendTooltip({ bucket, visible }: TrendTooltipProps) {
  const rows: { key: SeriesKey; label: string }[] = [
    { key: "conversations", label: m.home_trend_series_conversations() },
    { key: "aiResolved", label: m.home_trend_series_ai_resolved() },
  ];

  return (
    <div className="rounded-[var(--radius)] border border-[var(--border-surface-default)] bg-[var(--bg-surface-base)] px-2.5 py-2 [box-shadow:var(--shadow-overlay)]">
      <div className="mb-1.5 border-b border-[var(--border-surface-default)] pb-1.5 font-mono text-micro text-[var(--content-fg-subtle)]">
        {formatSpan(bucket)}
      </div>
      {rows
        .filter((row) => visible[row.key])
        .map((row) => (
          <div className="flex items-center gap-1.5" key={row.key}>
            <span className={SERIES_SWATCH[row.key]} />
            <span className="text-label text-[var(--content-fg-subtle)]">
              {row.label}
            </span>
            <span className="ml-auto pl-3 font-mono text-micro [font-variant-numeric:tabular-nums] text-[var(--content-fg-strong)]">
              {bucket[row.key]}
            </span>
          </div>
        ))}
    </div>
  );
}

interface SeriesToggleProps {
  label: string;
  onFocusChange: (focused: boolean) => void;
  onToggle: () => void;
  pressed: boolean;
  series: SeriesKey;
}

function SeriesToggle({
  label,
  onFocusChange,
  onToggle,
  pressed,
  series,
}: SeriesToggleProps) {
  return (
    <button
      aria-pressed={pressed}
      className={LEGEND_CLASS}
      onBlur={() => {
        onFocusChange(false);
      }}
      onClick={onToggle}
      onFocus={() => {
        onFocusChange(true);
      }}
      onMouseEnter={() => {
        onFocusChange(true);
      }}
      onMouseLeave={() => {
        onFocusChange(false);
      }}
      type="button"
    >
      <span className={SERIES_SWATCH[series]} />
      {label}
    </button>
  );
}

const FRAME_CLASS = "flex min-h-82.5 flex-col gap-3.5";
const TITLE_CLASS =
  "m-0 text-headline font-bold text-[var(--content-fg-strong)]";
const SUBTITLE_CLASS = "m-0 mt-1 text-label text-[var(--content-fg-subtle)]";

/** 数据没到之前先把卡片撑到最终高度，免得三张指标卡下面凭空长出一张图。 */
export function ConversationTrendLoading() {
  return (
    <Panel className={FRAME_CLASS} padding={18}>
      <div className="min-w-0">
        <h2 className={TITLE_CLASS}>{m.home_trend_title()}</h2>
        <p className={SUBTITLE_CLASS}>
          <Skeleton inline width={144} />
        </p>
      </div>
    </Panel>
  );
}

export interface ConversationTrendProps {
  period: Period;
  trend: HomeMetricsResponse["trend"];
}

/**
 * 趋势图：会话数与 AI 解决数两条线，图例可以单独收起一条，悬停出当段数值。
 * 近 30 天按周合计（GON-42），口径写在副标题上。
 */
export function ConversationTrend({ period, trend }: ConversationTrendProps) {
  const [visible, setVisible] = useState({
    aiResolved: true,
    conversations: true,
  });
  const [focused, setFocused] = useState<SeriesKey | null>(null);
  const { buckets, grain } = bucketTrend(trend, period);
  const ticks = readTicks(buckets);
  const dateTicks = readDateTicks(buckets);
  const showDots = buckets.length <= MAX_DOTS;
  const lastIndex = buckets.length - 1;
  const firstBucket = buckets[0];
  const lastBucket = buckets[lastIndex];
  const readSubtitle =
    grain === "week"
      ? m.home_trend_subtitle_weekly
      : m.home_trend_subtitle_daily;
  const chartLabel =
    grain === "week"
      ? m.home_trend_chart_label_weekly()
      : m.home_trend_chart_label_daily();

  function toggleSeries(series: SeriesKey): void {
    setVisible((current) => {
      const next = { ...current, [series]: !current[series] };
      return next.aiResolved || next.conversations ? next : current;
    });
  }

  /** 悬停图例时把另一条压淡，不改显示状态——单独看一条不该要点两次。 */
  function readOpacity(series: SeriesKey): number {
    return focused === null || focused === series ? 1 : DIMMED_OPACITY;
  }

  function focusSeries(series: SeriesKey, isFocused: boolean): void {
    setFocused((current) =>
      isFocused ? series : current === series ? null : current,
    );
  }

  return (
    <Panel className={FRAME_CLASS} padding={18}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 shrink grow basis-50">
          <h2 className={TITLE_CLASS}>{m.home_trend_title()}</h2>
          {firstBucket && lastBucket && (
            <p className={SUBTITLE_CLASS}>
              {readSubtitle({
                end: formatDay(lastBucket.end),
                start: formatDay(firstBucket.start),
              })}
            </p>
          )}
        </div>
        <div className="flex flex-none items-center gap-1 rounded-[var(--radius)] border border-[var(--border-surface-default)] bg-[var(--bg-page-body)] p-0.75">
          <SeriesToggle
            label={m.home_trend_series_conversations()}
            onFocusChange={(isFocused) => {
              focusSeries("conversations", isFocused);
            }}
            onToggle={() => {
              toggleSeries("conversations");
            }}
            pressed={visible.conversations}
            series="conversations"
          />
          <SeriesToggle
            label={m.home_trend_series_ai_resolved()}
            onFocusChange={(isFocused) => {
              focusSeries("aiResolved", isFocused);
            }}
            onToggle={() => {
              toggleSeries("aiResolved");
            }}
            pressed={visible.aiResolved}
            series="aiResolved"
          />
        </div>
      </div>
      {/* min-h-55 是设计稿给画布区的 220px 下限，卡片的 min-h-82.5 撑不满时兜住它。 */}
      <div aria-label={chartLabel} className="relative min-h-55 flex-1">
        {/* flex 撑出来的高度不是 definite height，Recharts 的 height="100%" 会解析成 0；
            绝对定位后高度由 inset 决定，才量得到。 */}
        <ResponsiveContainer
          className="absolute inset-0"
          height="100%"
          width="100%"
        >
          <ComposedChart data={buckets} margin={CHART_MARGIN[grain]}>
            <CartesianGrid stroke={AXIS_COLOR} vertical={false} />
            <CartesianGrid
              horizontal={false}
              stroke={AXIS_COLOR}
              strokeDasharray={DASHED}
            />
            <XAxis
              axisLine={false}
              className="font-mono text-micro"
              dataKey="start"
              interval={0}
              tick={TICK_FILL}
              tickFormatter={formatDay}
              tickLine={false}
              tickMargin={8}
              ticks={dateTicks}
            />
            <YAxis
              axisLine={false}
              className="font-mono text-micro"
              domain={[0, ticks[ticks.length - 1] ?? AXIS_STEP]}
              tick={TICK_FILL}
              tickLine={false}
              ticks={ticks}
              width={AXIS_WIDTH}
            />
            <Tooltip
              content={({ active, label }) => {
                const bucket =
                  active && typeof label === "string"
                    ? buckets.find((item) => item.start === label)
                    : undefined;

                return bucket === undefined ? null : (
                  <TrendTooltip bucket={bucket} visible={visible} />
                );
              }}
              cursor={{ stroke: AXIS_COLOR, strokeDasharray: DASHED }}
            />
            {visible.conversations && (
              <Area
                activeDot={readDot(
                  "conversations",
                  DOT_RADIUS[grain] + ACTIVE_DOT_GROWTH,
                  readOpacity("conversations"),
                )}
                dataKey="conversations"
                dot={
                  showDots &&
                  readDot(
                    "conversations",
                    DOT_RADIUS[grain],
                    readOpacity("conversations"),
                  )
                }
                fill={SERIES_COLOR.conversations}
                fillOpacity={AREA_OPACITY * readOpacity("conversations")}
                isAnimationActive={false}
                stroke={SERIES_COLOR.conversations}
                strokeOpacity={readOpacity("conversations")}
                strokeWidth={LINE_WIDTH}
                type="linear"
              >
                {/* 只标最后一个周桶的会话数：两条都标的话，AI 解决率高的店里两个标签会
                    叠在一起，而「这个月是涨是跌」的主语是会话量，另一条交给 tooltip。 */}
                {grain === "week" && (
                  <LabelList
                    className="font-mono text-micro font-semibold"
                    {...END_LABEL_STYLE}
                    valueAccessor={(_entry, index) =>
                      // 不读 entry.value：Area 的点值是 [基线, 值] 数组，不是数字
                      index === lastIndex
                        ? (lastBucket?.conversations ?? null)
                        : null
                    }
                  />
                )}
              </Area>
            )}
            {visible.aiResolved && (
              <Line
                activeDot={readDot(
                  "aiResolved",
                  DOT_RADIUS[grain] + ACTIVE_DOT_GROWTH,
                  readOpacity("aiResolved"),
                )}
                dataKey="aiResolved"
                dot={
                  showDots &&
                  readDot(
                    "aiResolved",
                    DOT_RADIUS[grain],
                    readOpacity("aiResolved"),
                  )
                }
                isAnimationActive={false}
                stroke={SERIES_COLOR.aiResolved}
                strokeOpacity={readOpacity("aiResolved")}
                strokeWidth={LINE_WIDTH}
                type="linear"
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  );
}
